import React, { useMemo, useState } from "react"

import {
  yesNoBadgeColorClasses,
  yesNoBadgeShapeClasses,
} from "@/metrics/metric-display"
import { IvrFlowAdherenceConfig } from "@/metrics/registry/configuration"
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@coval/ui"
import {
  AlertTriangle,
  ArrowDown,
  CheckCircle2,
  Network,
  XCircle,
} from "lucide-react"

import { flowToReactFlow } from "@/components/ivr-flow/ivr-flow-utils"
import { IVRFlowPreview } from "@/components/ivr-flow/IVRFlowPreview"
import type { IVRFlow } from "@/types/IVRFlow"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"

interface NodeVisitResult {
  node_id: string
  expected_prompt: string
  actual_prompt: string
  matched: boolean
  match_ratio: number
  message_index?: number
}

interface DtmfInput {
  from_node: string
  digits: string
  matched_branch: string | null
  next_node: string | null
}

interface FlowTerminatedAt {
  node_id: string
  reason: string
}

interface IvrFlowAdherenceResultJson {
  score: number
  match_threshold?: number
  total_nodes_visited: number
  nodes_correct: number
  nodes_visited: NodeVisitResult[]
  dtmf_inputs: DtmfInput[]
  first_divergence: NodeVisitResult | null
  flow_terminated_at: FlowTerminatedAt | null
}

const isIvrFlowAdherenceResult = (
  value: unknown,
): value is IvrFlowAdherenceResultJson => {
  return (
    typeof value === "object" &&
    value !== null &&
    "nodes_visited" in value &&
    Array.isArray((value as IvrFlowAdherenceResultJson).nodes_visited) &&
    "dtmf_inputs" in value &&
    Array.isArray((value as IvrFlowAdherenceResultJson).dtmf_inputs)
  )
}

// ─── helpers ───────────────────────────────────────────────────────────────

const scoreTone = (
  score: number,
): { ring: string; bg: string; text: string; label: string } => {
  if (score >= 0.999) {
    return {
      ring: "ring-emerald-400/40",
      bg: "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800",
      text: "text-emerald-700 dark:text-emerald-300",
      label: "Perfect adherence",
    }
  }
  if (score >= 0.75) {
    return {
      ring: "ring-amber-400/40",
      bg: "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800",
      text: "text-amber-700 dark:text-amber-300",
      label: "Mostly adhered",
    }
  }
  if (score > 0) {
    return {
      ring: "ring-orange-400/40",
      bg: "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800",
      text: "text-orange-700 dark:text-orange-300",
      label: "Partial adherence",
    }
  }
  return {
    ring: "ring-rose-400/40",
    bg: "bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800",
    text: "text-rose-700 dark:text-rose-300",
    label: "Did not adhere",
  }
}

const formatReason = (reason: string): string =>
  reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())

// ─── building blocks ───────────────────────────────────────────────────────

const ScoreHero: React.FC<{ result: IvrFlowAdherenceResultJson }> = ({
  result,
}) => {
  const tone = scoreTone(result.score)
  const pct = (result.score * 100).toFixed(0)
  return (
    <div className={`rounded-lg border p-4 flex items-center gap-4 ${tone.bg}`}>
      <div
        className={`shrink-0 w-20 h-20 rounded-full flex items-center justify-center ring-4 bg-white dark:bg-gray-900 ${tone.ring}`}
      >
        <div className="text-center">
          <div className={`text-2xl font-bold tabular-nums ${tone.text}`}>
            {pct}
          </div>
          <div className={`text-[10px] uppercase tracking-wide ${tone.text}`}>
            score
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-semibold ${tone.text}`}>{tone.label}</div>
        <div className="text-xs text-coval-text-muted mt-0.5">
          {result.nodes_correct} of {result.total_nodes_visited} expected prompt
          {result.total_nodes_visited === 1 ? "" : "s"} matched
          {typeof result.match_threshold === "number" && (
            <>
              {" "}
              · match threshold{" "}
              <span className="font-mono">
                {result.match_threshold.toFixed(2)}
              </span>
            </>
          )}
        </div>
        {result.flow_terminated_at && (
          <div className="text-xs text-coval-text-muted mt-1">
            Terminated at{" "}
            <span className="font-mono text-coval-text-muted">
              {result.flow_terminated_at.node_id}
            </span>{" "}
            <span className="text-coval-text-muted">
              ({formatReason(result.flow_terminated_at.reason)})
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

const DivergenceCallout: React.FC<{
  divergence: NodeVisitResult
}> = ({ divergence }) => (
  <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3 space-y-2">
    <div className="flex items-center gap-2">
      <AlertTriangle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
      <div className="text-sm font-semibold text-rose-700 dark:text-rose-300">
        First divergence at{" "}
        <span className="font-mono">{divergence.node_id}</span>
      </div>
      <span className="ml-auto text-[10px] tabular-nums text-rose-600 dark:text-rose-400">
        match ratio {divergence.match_ratio.toFixed(2)}
      </span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div className="rounded-md bg-white dark:bg-gray-900 border border-rose-200/60 dark:border-rose-800/60 p-2">
        <div className="text-[10px] uppercase tracking-wide text-coval-text-muted mb-1">
          Expected
        </div>
        <div className="text-xs text-coval-text-muted whitespace-pre-wrap break-words">
          {divergence.expected_prompt || "—"}
        </div>
      </div>
      <div className="rounded-md bg-white dark:bg-gray-900 border border-rose-200/60 dark:border-rose-800/60 p-2">
        <div className="text-[10px] uppercase tracking-wide text-coval-text-muted mb-1">
          Actual
        </div>
        <div className="text-xs text-coval-text-muted whitespace-pre-wrap break-words">
          {divergence.actual_prompt || "—"}
        </div>
      </div>
    </div>
  </div>
)

const NodeRow: React.FC<{ node: NodeVisitResult; isDivergence: boolean }> = ({
  node,
  isDivergence,
}) => {
  const tone = node.matched
    ? {
        ring: "border-emerald-200 dark:border-emerald-800",
        bg: "bg-white dark:bg-gray-900",
        badge:
          "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
        icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
      }
    : {
        ring: isDivergence
          ? "border-rose-300 dark:border-rose-700 ring-1 ring-rose-300/40"
          : "border-rose-200 dark:border-rose-800",
        bg: "bg-rose-50 dark:bg-rose-900/10",
        badge:
          "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
        icon: <XCircle className="w-4 h-4 text-rose-600" />,
      }

  return (
    <div className={`rounded-md border p-3 space-y-2 ${tone.ring} ${tone.bg}`}>
      <div className="flex items-center gap-2">
        {tone.icon}
        <span className="font-mono text-xs text-coval-text-muted">
          {node.node_id}
        </span>
        <span
          className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${tone.badge}`}
        >
          {node.matched ? "PASS" : "FAIL"}
        </span>
        <span className="ml-auto text-[10px] tabular-nums text-coval-text-muted">
          ratio {node.match_ratio.toFixed(2)}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-coval-text-muted mb-0.5">
            Expected
          </div>
          <div className="text-xs text-coval-text-muted whitespace-pre-wrap break-words">
            {node.expected_prompt || "—"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-coval-text-muted mb-0.5">
            Actual
          </div>
          <div className="text-xs text-coval-text-muted whitespace-pre-wrap break-words">
            {node.actual_prompt || "—"}
          </div>
        </div>
      </div>
    </div>
  )
}

const DtmfRow: React.FC<{ input: DtmfInput }> = ({ input }) => {
  const matched = !!input.matched_branch && !!input.next_node
  return (
    <div className="flex items-center gap-2 text-xs pl-2">
      <ArrowDown className="w-3.5 h-3.5 text-coval-text-muted shrink-0" />
      <span className="text-coval-text-muted shrink-0">Caller pressed</span>
      <span className="font-mono px-1.5 py-0.5 rounded bg-surface-1 text-coval-text-muted">
        {input.digits || "(silence)"}
      </span>
      <span className="text-coval-text-muted shrink-0">→</span>
      {matched ? (
        <>
          <span className="text-coval-text-muted shrink-0">matched</span>
          <span className="font-mono px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
            {input.matched_branch}
          </span>
          <span className="text-coval-text-muted shrink-0">→</span>
          <span className="font-mono text-coval-text-muted truncate">
            {input.next_node}
          </span>
        </>
      ) : (
        <span className="text-rose-600 dark:text-rose-400 font-medium">
          no matching branch
        </span>
      )}
    </div>
  )
}

const WalkTimeline: React.FC<{
  result: IvrFlowAdherenceResultJson
}> = ({ result }) => {
  // Interleave DTMF inputs with node visits. Each DTMF input fires after the
  // node it was sent from; render it right after that node's row.
  const inputsByFromNode = new Map<string, DtmfInput[]>()
  for (const input of result.dtmf_inputs) {
    const list = inputsByFromNode.get(input.from_node) ?? []
    list.push(input)
    inputsByFromNode.set(input.from_node, list)
  }
  const seenInputs = new Map<string, number>()
  const divergenceId = result.first_divergence?.node_id

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-coval-text-muted">Walk</h3>
      <div className="space-y-2">
        {result.nodes_visited.map((node, index) => {
          const seen = seenInputs.get(node.node_id) ?? 0
          const nextInput = inputsByFromNode.get(node.node_id)?.[seen]
          if (nextInput) {
            seenInputs.set(node.node_id, seen + 1)
          }
          return (
            <React.Fragment key={`${node.node_id}-${index}`}>
              <NodeRow
                node={node}
                isDivergence={node.node_id === divergenceId}
              />
              {nextInput && <DtmfRow input={nextInput} />}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

const EmptyState: React.FC = () => (
  <div className="rounded-lg border border-dashed border-coval-border p-6 text-center text-sm text-coval-text-muted">
    The IVR flow never entered — no agent prompts matched the start node.
  </div>
)

const SeeTreeButton: React.FC<{
  flow: IVRFlow
  result: IvrFlowAdherenceResultJson
}> = ({ flow, result }) => {
  const [open, setOpen] = useState(false)

  // Build node id → outcome (passed | failed | unvisited) so we can color
  // the tree by what actually happened during the walk.
  const outcomeById = useMemo(() => {
    const map = new Map<string, "passed" | "failed">()
    for (const visit of result.nodes_visited) {
      map.set(visit.node_id, visit.matched ? "passed" : "failed")
    }
    return map
  }, [result.nodes_visited])

  const { nodes: rfNodes, edges: rfEdges } = useMemo(
    () => flowToReactFlow(flow),
    [flow],
  )

  // Tint visited nodes via inline style; ReactFlow merges this on the
  // rendered node DOM. Unvisited nodes are faded to draw the eye to the path.
  const styledNodes = useMemo(
    () =>
      rfNodes.map((n) => {
        const outcome = outcomeById.get(n.id)
        if (outcome === "passed") {
          return {
            ...n,
            style: {
              ...n.style,
              boxShadow: "0 0 0 3px rgba(16,185,129,0.35)",
              borderRadius: 6,
            },
          }
        }
        if (outcome === "failed") {
          return {
            ...n,
            style: {
              ...n.style,
              boxShadow: "0 0 0 3px rgba(244,63,94,0.45)",
              borderRadius: 6,
            },
          }
        }
        return { ...n, style: { ...n.style, opacity: 0.45 } }
      }),
    [rfNodes, outcomeById],
  )

  return (
    <>
      <div className="flex justify-start">
        <Button type="button" variant="secondary" onClick={() => setOpen(true)}>
          <Network className="w-4 h-4 mr-1" />
          See tree
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[1100px] w-[95vw] max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>IVR Flow</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-coval-text-muted">
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/70" />
                Passed
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-rose-500/70" />
                Failed
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-sm bg-surface-2" />
                Not visited
              </span>
              <span className="ml-auto">
                {flow.nodes.length} node{flow.nodes.length === 1 ? "" : "s"} ·{" "}
                {flow.edges.length} edge{flow.edges.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="h-[60vh]">
              <IVRFlowPreview nodes={styledNodes} edges={rfEdges} />
            </div>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ─── main record ───────────────────────────────────────────────────────────

export class IvrFlowAdherenceMetricRecord extends NumericalMetricRecord {
  private static readonly METRIC_TYPE_LABEL = "IVR Flow Adherence"

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    // Backend writes the typed Result subclass to `mo.result.raw_values`.
    // Fall back to the deprecated `mo.result_json` for older runs.
    const rawValues =
      (mo.result?.raw_values as unknown) ?? (mo.result_json as unknown) ?? null
    if (!isIvrFlowAdherenceResult(rawValues)) {
      return (
        <div className="text-sm text-foreground">
          <strong>Result</strong>: {mo.float_value ?? "N/A"}
        </div>
      )
    }

    const flow = this.metric_metadata?.ivr_flow as IVRFlow | undefined

    return (
      <div className="space-y-4">
        <ScoreHero result={rawValues} />
        {rawValues.first_divergence && (
          <DivergenceCallout divergence={rawValues.first_divergence} />
        )}
        {rawValues.nodes_visited.length > 0 ? (
          <WalkTimeline result={rawValues} />
        ) : (
          <EmptyState />
        )}
        {flow && Array.isArray(flow.nodes) && flow.nodes.length > 0 && (
          <SeeTreeButton flow={flow} result={rawValues} />
        )}
      </div>
    )
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <IvrFlowAdherenceConfig
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }

  renderMetricType(): React.ReactNode {
    return IvrFlowAdherenceMetricRecord.METRIC_TYPE_LABEL
  }

  // Color the scorecard chip green on perfect adherence (score === 1) and red
  // otherwise. Adherence is binary in practice — any divergence is a failure.
  _renderSimulationCard(mo: RunMetricOutput): React.ReactNode {
    const value = mo.float_value
    const colorClass =
      value == null
        ? yesNoBadgeColorClasses.default
        : value >= 0.999
          ? yesNoBadgeColorClasses.success
          : yesNoBadgeColorClasses.danger
    const display = value == null ? "--" : value.toFixed(2)
    return (
      <span className={`${yesNoBadgeShapeClasses} ${colorClass}`}>
        {display}
      </span>
    )
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    const flow = metricRecordData.metric_metadata?.ivr_flow
    const nodeCount = Array.isArray(flow?.nodes) ? flow.nodes.length : 0
    const edgeCount = Array.isArray(flow?.edges) ? flow.edges.length : 0
    const startNode = flow?.start_node ?? "Not set"
    const threshold =
      typeof metricRecordData.metric_metadata?.match_threshold === "number"
        ? metricRecordData.metric_metadata.match_threshold
        : 0.85

    return (
      <div className="flex flex-col gap-2">
        <div>
          <h3 className="font-semibold">Start node:</h3>
          <p className="font-mono text-sm text-coval-text-muted">{startNode}</p>
        </div>
        <div>
          <h3 className="font-semibold">Flow size:</h3>
          <p className="text-sm">
            {nodeCount} node{nodeCount === 1 ? "" : "s"}, {edgeCount} edge
            {edgeCount === 1 ? "" : "s"}
          </p>
        </div>
        <div>
          <h3 className="font-semibold">Match threshold:</h3>
          <p className="text-sm">{threshold}</p>
        </div>
      </div>
    )
  }
}
